import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";
import TextInput from "components/app/function/TextInput";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import { Textarea } from "components/ui/textarea";
import { Loader, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AppLog } from "services";
import appStore from "store/appStore";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { Service } from "utils/services";

interface DialogProps {
  onUploadSuccess: ({ res, name }: { res: any; name: string }) => void;
  musicAlbumItem?: any;
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean) => void;
}

interface updateDatasProps {
  sub_title?: string;
  name?: string;
}

export default function DialogEditAndAddMusicAlbum({ onUploadSuccess, musicAlbumItem, isDialogOpen, setIsDialogOpen }: DialogProps) {
  const [updateDatas, setUpdateDatas] = useState<updateDatasProps>({
    sub_title: "",
    name: "",
  });
  const { showDialogConfirm } = appStore();
  const [loadingSubmitUpdate, setLoadingSubmitUpdate] = useState<boolean>(false);
  const [loadingSubmitDelete, setLoadingSubmitDelete] = useState<boolean>(false);

  useEffect(() => {
    setUpdateDatas({
      sub_title: musicAlbumItem?.sub_title ?? "",
      name: musicAlbumItem?.name ?? "",
    });
  }, [musicAlbumItem]);

  const onSubmitInsertUserSoundAlbum = async () => {
    setLoadingSubmitUpdate(true);
    try {
      let url: string = "";
      url = RouteApi.submitInsertUserSoundAlbum;
      url += `token=${Service.token}`;

      const data = {
        name: updateDatas.name,
        sub_title: updateDatas.sub_title,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        onUploadSuccess({
          res: response.music_album,
          name: "insert",
        });
        setIsDialogOpen(false);
      } else {
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitInsertUserSoundAlbum]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingSubmitUpdate(false);
    }
  };

  const onSubmitUpdateUserSoundAlbum = async () => {
    setLoadingSubmitUpdate(true);
    try {
      let url: string = "";
      url = RouteApi.submitUpdateUserSoundAlbum;
      url += `token=${Service.token}`;
      const data = {
        unid: musicAlbumItem?.id,
        name: updateDatas.name,
        sub_title: updateDatas.sub_title,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        onUploadSuccess({
          res: response.music_album,
          name: "insert",
        });
        setIsDialogOpen(false);
      } else {
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitUpdateUserSoundAlbum]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingSubmitUpdate(false);
    }
  };

  const onSubmitDeleteMusicAlbum = async () => {
    setLoadingSubmitDelete(true);
    try {
      let url: string = "";
      url = RouteApi.submitDeleteUserSoundAlbum;
      url += `token=${Service.token}`;
      const data = {
        unid: musicAlbumItem.id,
      };

      const response = await RequestApi(url, { data });
      if (response.status === "success") {
        onUploadSuccess({
          res: response.music_album,
          name: "delete",
        });
        setIsDialogOpen(false);
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitDeleteMusicAlbum]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingSubmitDelete(false);
    }
  };

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="md:max-w-[800px] flex flex-col justify-between">
        <DialogHeader>
          <DialogTitle>{musicAlbumItem ? "Edit Music Album" : "Create New Music Album"}</DialogTitle>
          <DialogDescription>
            {musicAlbumItem
              ? `Click "Save" when
      you finished.`
              : `Click "Save" when
      you finished.`}
          </DialogDescription>
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>
        <ScrollArea className="w-full h-[400px] pr-[20px] space-y-2">
          <div className="mx-2 space-y-2">
            <Label className="">Name</Label>
            <TextInput
              value={updateDatas?.name}
              onChange={(event) => {
                setUpdateDatas((prev) => ({
                  ...prev,
                  name: event.target.value,
                }));
              }}
            />
          </div>

          <div className="mx-2 space-y-2 mt-2">
            <Label className="">Sub Title</Label>
            <Textarea
              value={updateDatas.sub_title}
              onChange={(event) => {
                setUpdateDatas((prev) => ({
                  ...prev,
                  sub_title: event.target.value,
                }));
              }}
            />
          </div>

          <ScrollBar orientation="vertical" />
        </ScrollArea>
        <DialogFooter className=" flex justify-between gap-3">
          {musicAlbumItem ? (
            <>
              <Button
                onClick={() =>
                  showDialogConfirm({
                    title: "Delete",
                    message: "Do you want to delete ?",
                    onSubmit() {
                      onSubmitDeleteMusicAlbum();
                    },
                  })
                }
                className="w-full text-white mt-3 bg-red-600 hover:bg-red-500 outline-none border-0 focus:border-yellow-300"
              >
                {loadingSubmitDelete ? <Loader className="animate-spin" /> : "Delete"}
              </Button>

              <Button onClick={onSubmitUpdateUserSoundAlbum} className="w-full text-white mt-3 bg-blue-600 hover:bg-blue-500 outline-none border-none">
                {loadingSubmitUpdate ? <Loader className="animate-spin" /> : "Save"}
              </Button>
            </>
          ) : (
            <Button onClick={onSubmitInsertUserSoundAlbum} className="w-full text-white mt-3 bg-blue-600 hover:bg-blue-500">
              {loadingSubmitUpdate ? <Loader className="animate-spin" /> : "Save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
